import { Input } from "@/components/ui/input";
import { DualRangeSlider } from "../ui/dual-range-slider";
import { useGetAccountSettings } from "@/tanstack-hooks";
import { PlatformUnits } from "@/generated-api/models/PlatformUnits";
import { cmToFeetInches } from "../onboarding/data-form/HeightSelector";

export const MIN_HEIGHT = 130;
export const MAX_HEIGHT = 220;

export default function HeightRangeSelector({
  value,
  onChange,
}: {
  value: number[] | undefined;
  onChange: (value: (number | null)[]) => void;
}) {
  const accountSettingsQuery = useGetAccountSettings();
  const units: PlatformUnits | undefined =
    accountSettingsQuery.data?.preferred_units;
  const isMetric = units !== PlatformUnits.US;

  const handleHeightChange = (sliderValue: number[]) => {
    onChange(sliderValue);
  };

  const handleInputChangeMin = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue)) {
      onChange([null, value ? value[1] : MAX_HEIGHT]);
      return;
    }
    onChange([newValue, value ? value[1] : MAX_HEIGHT]);
  };

  const handleInputChangeMax = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue)) {
      onChange([value ? value[0] : MIN_HEIGHT, null]);
      return;
    }
    onChange([value ? value[0] : MIN_HEIGHT, newValue]);
  };

  const handleBlurMax = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue) || newValue < MIN_HEIGHT) {
      onChange([value ? value[0] : MIN_HEIGHT, MIN_HEIGHT]);
      return;
    } else if (newValue > MAX_HEIGHT) {
      onChange([value ? value[0] : MIN_HEIGHT, MAX_HEIGHT]);
      return;
    }
  };

  const handleBlurMin = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue) || newValue < MIN_HEIGHT) {
      onChange([MIN_HEIGHT, value ? value[1] : MAX_HEIGHT]);
      return;
    } else if (newValue > MAX_HEIGHT) {
      onChange([MAX_HEIGHT, value ? value[1] : MAX_HEIGHT]);
      return;
    }
  };

  return (
    <div className="flex flex-row items-center w-full max-w-2xl">
      <div className="flex flex-row w-full gap-2 items-center">
        <DualRangeSlider
          value={value ? [value[0], value[1]] : [MIN_HEIGHT, MAX_HEIGHT]}
          onValueChange={handleHeightChange}
          max={MAX_HEIGHT}
          min={MIN_HEIGHT}
          step={1}
          className="flex-grow hover:cursor-pointer"
        />
        <div className="flex items-center flex-shrink-0">
          {isMetric ? (
            <Input
              type="number"
              inputMode="numeric"
              value={value ? value[0] : ""}
              onChange={handleInputChangeMin}
              onBlur={handleBlurMin}
              className="w-10 px-0 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          ) : (
            <span className="w-10 text-center">
              {value ? cmToFeetInches(value[0]) : cmToFeetInches(MIN_HEIGHT)}
            </span>
          )}
        </div>
        <span className="text-sm text-muted-foreground">-</span>
        <div className="flex items-center flex-shrink-0">
          {isMetric ? (
            <Input
              type="number"
              inputMode="numeric"
              value={value ? value[1] : ""}
              onChange={handleInputChangeMax}
              onBlur={handleBlurMax}
              className="w-10 px-0 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          ) : (
            <span className="w-10 text-center">
              {value ? cmToFeetInches(value[1]) : cmToFeetInches(MAX_HEIGHT)}
            </span>
          )}
        </div>
      </div>
      {isMetric && (
        <span className="text-nowrap text-gray-400 w-fit ml-1">cm</span>
      )}
    </div>
  );
}
